import { useState, useRef, useEffect } from 'react';

const T = {
  bg:'#06060d',s1:'#0d0c18',s2:'#12111e',s3:'#181628',
  border:'rgba(255,255,255,0.07)',border2:'rgba(255,255,255,0.13)',
  gold:'#d4a853',gold2:'#f0c878',cream:'#f0ead8',
  text2:'#9890b0',text3:'#55506a',teal:'#1a8a76',
  error:'#ff6b6b',rust:'#e74c3c',purple:'#8b5cf6',
};

// ── BOTTOM SHEET ─────────────────────────────────────────────
export function BottomSheet({ open, onClose, title, children, height='auto' }) {
  const sheetRef = useRef(null);
  const startY = useRef(null);
  const currentY = useRef(0);

  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const handleTouchStart = (e) => { startY.current = e.touches[0].clientY; };
  const handleTouchMove = (e) => {
    const delta = e.touches[0].clientY - startY.current;
    if (delta > 0 && sheetRef.current) {
      currentY.current = delta;
      sheetRef.current.style.transform = `translateY(${delta}px)`;
    }
  };
  const handleTouchEnd = () => {
    if (currentY.current > 100) { onClose(); }
    else if (sheetRef.current) { sheetRef.current.style.transform = 'translateY(0)'; }
    currentY.current = 0;
  };

  if (!open) return null;

  return (
    <div style={{position:'fixed',inset:0,zIndex:1000,display:'flex',flexDirection:'column',justifyContent:'flex-end'}}>
      {/* Backdrop */}
      <div onClick={onClose} style={{position:'absolute',inset:0,background:'rgba(0,0,0,0.6)',backdropFilter:'blur(4px)'}}/>
      {/* Sheet */}
      <div ref={sheetRef} style={{position:'relative',background:T.s1,borderRadius:'20px 20px 0 0',maxHeight:'90vh',overflow:'hidden',transition:'transform 0.2s ease',paddingBottom:'env(safe-area-inset-bottom)'}}>
        {/* Drag handle */}
        <div onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}
          style={{padding:'12px',display:'flex',justifyContent:'center',cursor:'grab'}}>
          <div style={{width:36,height:4,borderRadius:2,background:T.border2}}/>
        </div>
        {title && (
          <div style={{padding:'0 16px 12px',display:'flex',justifyContent:'space-between',alignItems:'center',borderBottom:`1px solid ${T.border}`}}>
            <div style={{fontSize:16,fontWeight:700,color:T.cream}}>{title}</div>
            <button onClick={onClose} style={{background:'none',border:'none',color:T.text3,fontSize:20,cursor:'pointer',padding:'4px 8px'}}>×</button>
          </div>
        )}
        <div style={{overflowY:'auto',maxHeight:'80vh'}}>
          {children}
        </div>
      </div>
    </div>
  );
}

// ── CREATE SHEET ─────────────────────────────────────────────
export function CreateSheet({ open, onClose, onNavigate, onPost }) {
  const options = [
    {icon:'📝',label:'New Post',sub:'Share with your network',action:()=>{onClose();onPost&&onPost();}},
    {icon:'🎬',label:'New Self-Tape',sub:'Record or upload a tape',action:()=>{onClose();onNavigate('selftape');}},
    {icon:'🎨',label:'New Vizion Board',sub:'Start pre-production',action:()=>{onClose();onNavigate('vizion');}},
    {icon:'🎞',label:'New Curtainz Project',sub:'Start post-production',action:()=>{onClose();onNavigate('curtainz');}},
    {icon:'📋',label:'Log Audition',sub:'Track your submissions',action:()=>{onClose();onNavigate('auditions');}},
  ];
  return (
    <BottomSheet open={open} onClose={onClose} title="Create">
      <div style={{padding:'8px 0 24px'}}>
        {options.map((o,i)=>(
          <button key={i} onClick={o.action} style={{width:'100%',background:'none',border:'none',padding:'14px 20px',display:'flex',alignItems:'center',gap:16,cursor:'pointer',textAlign:'left',borderBottom:i<options.length-1?`1px solid ${T.border}`:'none'}}>
            <div style={{width:48,height:48,borderRadius:14,background:T.s3,display:'flex',alignItems:'center',justifyContent:'center',fontSize:22,flexShrink:0}}>{o.icon}</div>
            <div>
              <div style={{fontSize:15,fontWeight:600,color:T.cream,fontFamily:"'DM Sans','Jost',sans-serif"}}>{o.label}</div>
              <div style={{fontSize:12,color:T.text3,marginTop:2,fontFamily:"'DM Sans','Jost',sans-serif"}}>{o.sub}</div>
            </div>
            <div style={{marginLeft:'auto',color:T.text3,fontSize:18}}>›</div>
          </button>
        ))}
      </div>
    </BottomSheet>
  );
}

// ── MORE SHEET ───────────────────────────────────────────────
export function MoreSheet({ open, onClose, onNavigate, currentView }) {
  const items = [
    {icon:'🎭',label:'Casting',id:'casting'},
    {icon:'💼',label:'Job Board',id:'jobs'},
    {icon:'🤝',label:'Connections',id:'connections'},
    {icon:'📋',label:'Auditions',id:'auditions'},
    {icon:'📄',label:'Contracts',id:'contracts'},
    {icon:'👥',label:'Roster',id:'roster'},
    {icon:'📹',label:'Self-Tape',id:'selftape'},
    {icon:'📊',label:'Analytics',id:'analytics'},
    {icon:'⧫',label:'Foundation',id:'foundation'},
    {icon:'⚙️',label:'Settings',id:'settings'},
  ];
  return (
    <BottomSheet open={open} onClose={onClose} title="More">
      <div style={{padding:'8px 0 24px',display:'grid',gridTemplateColumns:'1fr 1fr',gap:0}}>
        {items.map((item,i)=>(
          <button key={item.id} onClick={()=>{onClose();onNavigate(item.id);}} style={{background:currentView===item.id?`${T.gold}15`:'none',border:'none',padding:'16px 20px',display:'flex',alignItems:'center',gap:12,cursor:'pointer',textAlign:'left',borderBottom:`1px solid ${T.border}`,borderRight:i%2===0?`1px solid ${T.border}`:'none'}}>
            <span style={{fontSize:20}}>{item.icon}</span>
            <span style={{fontSize:14,fontWeight:500,color:currentView===item.id?T.gold:T.cream,fontFamily:"'DM Sans','Jost',sans-serif"}}>{item.label}</span>
          </button>
        ))}
      </div>
    </BottomSheet>
  );
}

// ── FILTER SHEET ─────────────────────────────────────────────
export function FilterSheet({ open, onClose, title, children }) {
  return (
    <BottomSheet open={open} onClose={onClose} title={title||'Filter'}>
      <div style={{padding:'16px 20px 32px'}}>
        {children}
        <button onClick={onClose} style={{width:'100%',marginTop:16,padding:'14px',background:`linear-gradient(135deg,${T.gold},${T.gold2})`,border:'none',borderRadius:12,fontSize:15,fontWeight:700,color:T.bg,cursor:'pointer',fontFamily:"'DM Sans','Jost',sans-serif"}}>
          Apply Filters
        </button>
      </div>
    </BottomSheet>
  );
}

// ── SKELETON LOADER ──────────────────────────────────────────
export function SkeletonCard({ lines=3 }) {
  return (
    <div style={{background:T.s2,borderRadius:16,padding:'16px',marginBottom:12,border:`1px solid ${T.border}`}}>
      <div style={{display:'flex',gap:12,marginBottom:12}}>
        <div style={{width:44,height:44,borderRadius:'50%',background:T.s3,animation:'shimmer 1.5s infinite'}}/>
        <div style={{flex:1}}>
          <div style={{height:12,background:T.s3,borderRadius:6,marginBottom:8,width:'60%',animation:'shimmer 1.5s infinite'}}/>
          <div style={{height:10,background:T.s3,borderRadius:6,width:'40%',animation:'shimmer 1.5s infinite'}}/>
        </div>
      </div>
      {Array.from({length:lines}).map((_,i)=>(
        <div key={i} style={{height:10,background:T.s3,borderRadius:6,marginBottom:8,width:i===lines-1?'70%':'100%',animation:'shimmer 1.5s infinite'}}/>
      ))}
      <style>{`@keyframes shimmer{0%,100%{opacity:0.4}50%{opacity:0.8}}`}</style>
    </div>
  );
}

// ── MOBILE TAB BAR ───────────────────────────────────────────
export function MobileTabBar({ currentView, onNavigate, onCreateOpen, onMoreOpen }) {
  const tabs = [
    {id:'feed',      icon:'⌂', label:'Reel'},
    {id:'discover',  icon:'🔍',label:'Discover'},
    {id:'create',    icon:'✦', label:'Create',  isCreate:true},
    {id:'messages',  icon:'💬',label:'Inbox'},
    {id:'profile',   icon:'👤',label:'Me'},
  ];

  const discoverViews = ['search','talent','aisle'];
  const inboxViews = ['messages','notifications'];
  const profileViews = ['profile','settings'];

  const isActive = (tab) => {
    if (tab.id === 'feed') return currentView === 'feed';
    if (tab.id === 'discover') return discoverViews.includes(currentView);
    if (tab.id === 'messages') return inboxViews.includes(currentView);
    if (tab.id === 'profile') return profileViews.includes(currentView);
    return false;
  };

  return (
    <div style={{
      position:'fixed',bottom:0,left:0,right:0,
      background:T.s1,
      borderTop:`1px solid ${T.border}`,
      display:'flex',alignItems:'center',justifyContent:'space-around',
      height:60,
      paddingBottom:'env(safe-area-inset-bottom)',
      zIndex:500,
    }}>
      {tabs.map(tab=>{
        const active = isActive(tab);
        if (tab.isCreate) return (
          <button key={tab.id} onClick={onCreateOpen} style={{background:'none',border:'none',display:'flex',flexDirection:'column',alignItems:'center',gap:3,padding:'6px 12px',cursor:'pointer',minWidth:44}}>
            <div style={{width:44,height:44,borderRadius:'50%',background:`linear-gradient(135deg,${T.gold},${T.gold2})`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:20,color:T.bg,boxShadow:`0 4px 16px ${T.gold}50`,marginTop:-20}}>
              ✦
            </div>
          </button>
        );
        return (
          <button key={tab.id} onClick={()=>{
            if(tab.id==='discover') onNavigate('search');
            else if(tab.id==='messages') onNavigate('messages');
            else if(tab.id==='profile') onNavigate('profile');
            else onNavigate(tab.id);
          }} style={{background:'none',border:'none',display:'flex',flexDirection:'column',alignItems:'center',gap:3,padding:'6px 8px',cursor:'pointer',minWidth:44,flex:1}}>
            <span style={{fontSize:22,lineHeight:1}}>{tab.icon}</span>
            <span style={{fontSize:9,fontWeight:active?700:500,color:active?T.gold:T.text3,letterSpacing:'0.04em',textTransform:'uppercase',fontFamily:"'DM Sans','Jost',sans-serif"}}>{tab.label}</span>
            {active && <div style={{width:4,height:4,borderRadius:'50%',background:T.gold,position:'absolute',bottom:8}}/>}
          </button>
        );
      })}
    </div>
  );
}

export default MobileTabBar;
